import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './bookings.css';

const emptyForm = {
  user_id: '',
  event_id: '',
  name: '',
  email: '',
  tickets: 1,
  total_amount: 0,
  booking_status: 'pending',
  payment_status: 'unpaid',
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [bookingFilter, setBookingFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchEvents();
    fetchUsers();
    fetchBookings();
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [search, bookingFilter, paymentFilter]);

  const fetchEvents = async () => {
    const { data } = await supabase.from('events').select('id, title_en, price');
    setEvents(data || []);
  };

  const fetchUsers = async () => {
    const { data } = await supabase.from('users').select('id, name, email');
    setUsers(data || []);
  };

  const fetchBookings = async () => {
    setLoading(true);

    let query = supabase
      .from('bookings')
      .select(`
        *,
        users (
          name,
          email
        ),
        events (
          title_en,
          price
        )
      `)
      .order('created_at', { ascending: false });

    if (search) {
      query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`);
    }

    if (bookingFilter) {
      query = query.eq('booking_status', bookingFilter);
    }

    if (paymentFilter) {
      query = query.eq('payment_status', paymentFilter);
    }

    const { data, error } = await query;

    if (error) {
      alert(error.message);
    } else {
      setBookings(data || []);
    }

    setLoading(false);
  };

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter((b) => b.booking_status === 'confirmed').length,
    pending: bookings.filter((b) => b.booking_status === 'pending').length,
    revenue: bookings.reduce((sum, b) => sum + Number(b.total_amount || 0), 0),
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (booking) => {
    setForm({
      user_id: booking.user_id || '',
      event_id: booking.event_id || '',
      name: booking.name || '',
      email: booking.email || '',
      tickets: booking.tickets || 1,
      total_amount: booking.total_amount || 0,
      booking_status: booking.booking_status || 'pending',
      payment_status: booking.payment_status || 'unpaid',
    });

    setEditingId(booking.id);
    setShowModal(true);
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    const selectedUser = users.find((user) => String(user.id) === String(userId));

    setForm({
      ...form,
      user_id: userId,
      name: selectedUser?.name || '',
      email: selectedUser?.email || '',
    });
  };

  const handleEventChange = (e) => {
    const eventId = e.target.value;
    const selectedEvent = events.find((event) => String(event.id) === String(eventId));
    const ticketCount = Number(form.tickets || 1);

    setForm({
      ...form,
      event_id: eventId,
      total_amount: Number(selectedEvent?.price || 0) * ticketCount,
    });
  };

  const handleTicketsChange = (e) => {
    const tickets = Number(e.target.value || 1);
    const selectedEvent = events.find((event) => String(event.id) === String(form.event_id));

    setForm({
      ...form,
      tickets,
      total_amount: Number(selectedEvent?.price || 0) * tickets,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      user_id: form.user_id || null,
      event_id: form.event_id || null,
      name: form.name,
      email: form.email,
      tickets: Number(form.tickets || 1),
      total_amount: Number(form.total_amount || 0),
      booking_status: form.booking_status,
      payment_status: form.payment_status,
    };

    let result;

    if (editingId) {
      result = await supabase.from('bookings').update(payload).eq('id', editingId);
    } else {
      result = await supabase.from('bookings').insert([payload]);
    }

    if (result.error) {
      alert(result.error.message);
      return;
    }

    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    fetchBookings();
  };

  const deleteBooking = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this booking?');

    if (!confirmDelete) return;

    const { error } = await supabase.from('bookings').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchBookings();
  };

  const updateStatus = async (id, field, value) => {
    const { error } = await supabase
      .from('bookings')
      .update({ [field]: value })
      .eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchBookings();
  };

  return (
    <div className="dashboard-page">
      <Sidebar active="Bookings" />

      <main className="dashboard-main">
        <Topbar />

        <section className="bookings-content">
          <div className="bookings-header">
            <div>
              <h1>Bookings Management</h1>
              <p>Manage all event bookings and reservations</p>
            </div>

            <button className="add-booking-btn" onClick={openAddModal}>
              + Add Booking
            </button>
          </div>

          <div className="bookings-filter-card">
            <div className="booking-search">
              <span>⌕</span>
              <input
                placeholder="Search bookings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              value={bookingFilter}
              onChange={(e) => setBookingFilter(e.target.value)}
            >
              <option value="">All Booking Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
            >
              <option value="">All Payment Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="bookings-table-card">
            {loading ? (
              <p className="booking-loading">Loading bookings...</p>
            ) : (
              <div className="bookings-table-wrap">
                <table className="bookings-table">
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>User</th>
                      <th>Event</th>
                      <th>Tickets</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id}>
                        <td>#{String(booking.id).padStart(4, '0')}</td>

                        <td>
                          <strong>{booking.name}</strong>
                          <span>{booking.email}</span>
                        </td>

                        <td>{booking.events?.title_en || 'No event'}</td>

                        <td>{booking.tickets}</td>

                        <td>
                          {new Date(booking.created_at).toISOString().slice(0, 10)}
                        </td>

                        <td>${booking.total_amount}</td>

                        <td>
                          <select
                            className={`status-select ${booking.payment_status}`}
                            value={booking.payment_status}
                            onChange={(e) =>
                              updateStatus(booking.id, 'payment_status', e.target.value)
                            }
                          >
                            <option value="paid">paid</option>
                            <option value="unpaid">unpaid</option>
                            <option value="pending">pending</option>
                          </select>
                        </td>

                        <td>
                          <select
                            className={`status-select ${booking.booking_status}`}
                            value={booking.booking_status}
                            onChange={(e) =>
                              updateStatus(booking.id, 'booking_status', e.target.value)
                            }
                          >
                            <option value="confirmed">confirmed</option>
                            <option value="pending">pending</option>
                            <option value="cancelled">cancelled</option>
                          </select>
                        </td>

                        <td>
                          <div className="booking-actions">
                            <button onClick={() => openEditModal(booking)}>Edit</button>
                            <button onClick={() => deleteBooking(booking.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {bookings.length === 0 && (
                  <div className="empty-bookings">No bookings found.</div>
                )}
              </div>
            )}
          </div>

          <div className="booking-stats-grid">
            <div className="booking-stat-card">
              <div className="stat-badge pink">⌘</div>
              <div>
                <p>Total Bookings</p>
                <h2>{stats.total}</h2>
              </div>
            </div>

            <div className="booking-stat-card">
              <div className="stat-badge green">✓</div>
              <div>
                <p>Confirmed</p>
                <h2>{stats.confirmed}</h2>
              </div>
            </div>

            <div className="booking-stat-card">
              <div className="stat-badge yellow">▣</div>
              <div>
                <p>Pending</p>
                <h2>{stats.pending}</h2>
              </div>
            </div>

            <div className="booking-stat-card">
              <div className="stat-badge purple">♚</div>
              <div>
                <p>Total Revenue</p>
                <h2>${stats.revenue}</h2>
              </div>
            </div>
          </div>
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>

      {showModal && (
        <div className="booking-modal-overlay">
          <div className="booking-modal">
            <div className="booking-modal-header">
              <h2>{editingId ? 'Edit Booking' : 'Add Booking'}</h2>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form className="booking-form" onSubmit={handleSubmit}>
              <select value={form.user_id} onChange={handleUserChange}>
                <option value="">Select user</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} — {user.email}
                  </option>
                ))}
              </select>

              <select value={form.event_id} onChange={handleEventChange}>
                <option value="">Select event</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title_en} — ${event.price}
                  </option>
                ))}
              </select>

              <input
                name="name"
                placeholder="Name"
                value={form.name}
                onChange={handleChange}
                required
              />

              <input
                name="email"
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
              />

              <input
                name="tickets"
                type="number"
                min="1"
                placeholder="Tickets"
                value={form.tickets}
                onChange={handleTicketsChange}
                required
              />

              <input
                name="total_amount"
                type="number"
                placeholder="Total Amount"
                value={form.total_amount}
                onChange={handleChange}
              />

              <select
                name="payment_status"
                value={form.payment_status}
                onChange={handleChange}
              >
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="pending">Pending</option>
              </select>

              <select
                name="booking_status"
                value={form.booking_status}
                onChange={handleChange}
              >
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <div className="booking-modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit">
                  {editingId ? 'Save Changes' : 'Create Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;